import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Calculator,
  ArrowLeft,
  Check,
  Shield,
  BookOpen,
  Scale,
  Star,
  MapPin,
  Phone,
  Mail,
  Globe,
  Crown,
  Pencil,
  Eye,
  Save,
  Loader2,
  Inbox,
  Clock,
  CheckCircle,
  MessageCircle,
  User,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_OPTIONS = [
  { value: "enrolled_agent", label: "Enrolled Agent", icon: Shield },
  { value: "cpa", label: "Certified Public Accountant", icon: BookOpen },
  { value: "tax_attorney", label: "Tax Attorney", icon: Scale },
];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

function TierCard({ tier, info, selected, onSelect, current }) {
  const isCurrent = current === tier;
  const isLifetime = tier === "lifetime";
  const isYearly = tier === "yearly";
  const isMonthly = tier === "monthly";
  return (
    <Card
      className={`cursor-pointer transition-all duration-200 ${
        isLifetime ? "relative overflow-hidden " : ""
      }${
        selected === tier
          ? isLifetime
            ? "border-2 border-amber-500 shadow-lg ring-2 ring-amber-200"
            : "border-2 border-blue-500 shadow-lg ring-2 ring-blue-200"
          : isCurrent
          ? "border-2 border-green-400 bg-green-50/50"
          : isLifetime
          ? "border-2 border-amber-300 hover:border-amber-400 hover:shadow-lg bg-gradient-to-b from-amber-50/80 to-white"
          : "border border-slate-200 hover:border-slate-300 hover:shadow-md"
      }`}
      onClick={() => onSelect(tier)}
      data-testid={`tier-card-${tier}`}
    >
      <CardContent className="p-6">
        {isLifetime && (
          <div className="bg-red-600 text-white text-xs font-bold uppercase tracking-wider text-center py-1.5 -mx-6 -mt-6 mb-4 px-4 animate-pulse" data-testid="limited-time-banner">
            Limited Time Offer
          </div>
        )}
        {isYearly && (
          <div className="flex items-center gap-1.5 text-amber-600 font-semibold text-xs uppercase tracking-wide mb-3">
            <Crown className="w-4 h-4" />
            Save 40%
          </div>
        )}
        <h3 className={`text-xl font-bold ${isLifetime ? "text-amber-800" : "text-slate-800"}`}>{info.name}</h3>
        <div className="mt-2 mb-1">
          <span className={`text-3xl font-black ${isLifetime ? "text-amber-700" : "text-slate-800"}`}>
            ${info.price}
          </span>
          <span className="text-slate-500 text-sm">
            {isLifetime ? " one-time" : "/month"}
          </span>
        </div>
        {isLifetime && (
          <p className="text-xs text-amber-600 font-semibold mb-4">Pay once, listed forever</p>
        )}
        {isYearly && info.yearly_total && (
          <p className="text-xs text-slate-400 mb-4">Billed ${info.yearly_total}/year</p>
        )}
        {isMonthly && (
          <p className="text-xs text-slate-400 mb-4">Billed monthly</p>
        )}
        <ul className="space-y-2.5">
          {info.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
              <Check className={`w-4 h-4 ${isLifetime ? "text-amber-500" : "text-green-500"} mt-0.5 flex-shrink-0`} />
              {f}
            </li>
          ))}
        </ul>
        {isCurrent && (
          <div className="mt-4 text-center text-sm font-semibold text-green-600 bg-green-100 py-1.5 rounded-lg">
            Current Plan
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RegistrationForm({ token, onComplete }) {
  const [form, setForm] = useState({
    name: "", title: "", type: "enrolled_agent",
    city: "", state: "TX", phone: "", email: "",
    bio: "", years_experience: 1, website: "",
    specialties: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.city || !form.state || !form.phone || !form.email || !form.bio) {
      setError("Please fill in all required fields.");
      return;
    }
    setSaving(true);
    try {
      const specialties = form.specialties
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const typeOption = TYPE_OPTIONS.find((t) => t.value === form.type);
      const res = await axios.post(
        `${API}/directory/register-professional`,
        { ...form, specialties, title: typeOption?.label || form.title },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onComplete(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="registration-form">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name *</label>
          <Input
            value={form.name} onChange={(e) => handleChange("name", e.target.value)}
            placeholder="e.g., John Smith, CPA" data-testid="reg-name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Professional Type *</label>
          <select
            value={form.type} onChange={(e) => handleChange("type", e.target.value)}
            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
            data-testid="reg-type"
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">City *</label>
          <Input
            value={form.city} onChange={(e) => handleChange("city", e.target.value)}
            placeholder="e.g., Houston" data-testid="reg-city"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">State *</label>
          <select
            value={form.state} onChange={(e) => handleChange("state", e.target.value)}
            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
            data-testid="reg-state"
          >
            {US_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone *</label>
          <Input
            value={form.phone} onChange={(e) => handleChange("phone", e.target.value)}
            placeholder="(555) 123-4567" data-testid="reg-phone"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Contact Email *</label>
          <Input
            value={form.email} onChange={(e) => handleChange("email", e.target.value)}
            placeholder="you@example.com" data-testid="reg-email"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Years of Experience</label>
          <Input
            type="number" min="1" value={form.years_experience}
            onChange={(e) => handleChange("years_experience", parseInt(e.target.value) || 1)}
            data-testid="reg-experience"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Website</label>
          <Input
            value={form.website} onChange={(e) => handleChange("website", e.target.value)}
            placeholder="https://yoursite.com" data-testid="reg-website"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Specialties (comma-separated, max 5 for Basic tier)
        </label>
        <Input
          value={form.specialties}
          onChange={(e) => handleChange("specialties", e.target.value)}
          placeholder="e.g., Small Business Tax, IRS Representation, Schedule C"
          data-testid="reg-specialties"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Bio *</label>
        <textarea
          value={form.bio} onChange={(e) => handleChange("bio", e.target.value)}
          rows={4} placeholder="Tell potential clients about your experience and expertise..."
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-none"
          data-testid="reg-bio"
        />
      </div>

      <Button type="submit" disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11" data-testid="reg-submit">
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Profile...</> : "Create Professional Profile"}
      </Button>
    </form>
  );
}

function ProfileEditor({ profile, token, onUpdate }) {
  const [form, setForm] = useState({ ...profile, specialties: (profile.specialties || []).join(", ") });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const handleChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      const specialties = form.specialties.split(",").map((s) => s.trim()).filter(Boolean);
      const payload = {
        name: form.name, title: form.title, type: form.type,
        city: form.city, state: form.state, phone: form.phone,
        email: form.email, bio: form.bio, years_experience: form.years_experience,
        website: form.website, specialties,
      };
      const res = await axios.put(`${API}/directory/update-profile`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onUpdate(res.data);
      setMessage("Profile updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setMessage(err.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-editor">
      {message && (
        <div className={`px-4 py-3 rounded-lg text-sm ${
          message.includes("success") ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700"
        }`}>{message}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name</label>
          <Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} data-testid="edit-name" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Professional Type</label>
          <select
            value={form.type} onChange={(e) => handleChange("type", e.target.value)}
            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="edit-type"
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">City</label>
          <Input value={form.city} onChange={(e) => handleChange("city", e.target.value)} data-testid="edit-city" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">State</label>
          <select
            value={form.state} onChange={(e) => handleChange("state", e.target.value)}
            className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white" data-testid="edit-state"
          >
            {US_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone</label>
          <Input value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} data-testid="edit-phone" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Contact Email</label>
          <Input value={form.email} onChange={(e) => handleChange("email", e.target.value)} data-testid="edit-email" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Years of Experience</label>
          <Input
            type="number" min="1" value={form.years_experience}
            onChange={(e) => handleChange("years_experience", parseInt(e.target.value) || 1)} data-testid="edit-experience"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Website</label>
          <Input value={form.website || ""} onChange={(e) => handleChange("website", e.target.value)} data-testid="edit-website" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Specialties (comma-separated)</label>
        <Input value={form.specialties} onChange={(e) => handleChange("specialties", e.target.value)} data-testid="edit-specialties" />
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Bio</label>
        <textarea
          value={form.bio} onChange={(e) => handleChange("bio", e.target.value)}
          rows={4} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm resize-none" data-testid="edit-bio"
        />
      </div>

      <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white h-10" data-testid="save-profile">
        {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
      </Button>
    </div>
  );
}

function LeadsTab({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/directory/my-inquiries`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load inquiries:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (inquiryId) => {
    setTogglingId(inquiryId);
    try {
      const res = await axios.put(
        `${API}/directory/inquiries/${inquiryId}/status`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData((prev) => ({
        ...prev,
        inquiries: prev.inquiries.map((inq) =>
          inq.id === inquiryId ? { ...inq, status: res.data.status } : inq
        ),
        new_count: prev.inquiries.filter(
          (inq) => (inq.id === inquiryId ? res.data.status : inq.status) === "new"
        ).length,
      }));
    } catch (err) {
      console.error("Failed to update status:", err);
    } finally {
      setTogglingId(null);
    }
  };

  const STATUS_CONFIG = {
    new: { label: "New", color: "bg-blue-100 text-blue-700 border-blue-200", icon: Clock },
    read: { label: "Read", color: "bg-amber-100 text-amber-700 border-amber-200", icon: Eye },
    responded: { label: "Responded", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle },
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  const inquiries = data?.inquiries || [];

  return (
    <div className="space-y-4" data-testid="leads-tab">
      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-slate-800" data-testid="leads-total">{data?.total || 0}</div>
          <div className="text-xs text-slate-500 mt-1">Total Leads</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-blue-700" data-testid="leads-new">{data?.new_count || 0}</div>
          <div className="text-xs text-blue-600 mt-1">New</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-green-700" data-testid="leads-responded">
            {inquiries.filter((i) => i.status === "responded").length}
          </div>
          <div className="text-xs text-green-600 mt-1">Responded</div>
        </div>
      </div>

      {/* Inquiry list */}
      {inquiries.length === 0 ? (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-xl">
          <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-slate-600 mb-1">No leads yet</h3>
          <p className="text-sm text-slate-400">
            When visitors send you contact requests, they'll appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {inquiries.map((inq) => {
            const statusConf = STATUS_CONFIG[inq.status] || STATUS_CONFIG.new;
            const StatusIcon = statusConf.icon;
            return (
              <Card key={inq.id} className={`border ${inq.status === "new" ? "border-blue-200 bg-blue-50/30" : "border-slate-200"}`} data-testid={`lead-${inq.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <User className="w-5 h-5 text-slate-500" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-800 text-sm" data-testid={`lead-name-${inq.id}`}>{inq.name}</span>
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${statusConf.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusConf.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <a href={`mailto:${inq.email}`} className="hover:text-blue-600 flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {inq.email}
                          </a>
                          {inq.phone && (
                            <a href={`tel:${inq.phone}`} className="hover:text-blue-600 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {inq.phone}
                            </a>
                          )}
                        </div>
                        <div className="mt-2 bg-white border border-slate-100 rounded-lg p-3">
                          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-1">
                            <MessageCircle className="w-3 h-3" /> Tax Needs
                          </div>
                          <p className="text-sm text-slate-700 leading-relaxed" data-testid={`lead-message-${inq.id}`}>{inq.tax_needs}</p>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-slate-400">
                            {new Date(inq.created_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                          </span>
                          {inq.status !== "responded" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleStatus(inq.id)}
                              disabled={togglingId === inq.id}
                              className="text-xs h-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`toggle-status-${inq.id}`}
                            >
                              {togglingId === inq.id ? (
                                <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              ) : null}
                              Mark as {inq.status === "new" ? "Read" : "Responded"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}


export default function TaxProPortal() {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [profile, setProfile] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("profile"); // profile | subscription | leads | preview
  const [selectedTier, setSelectedTier] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [subMessage, setSubMessage] = useState("");

  useEffect(() => {
    if (!token) return;
    loadData();
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [profileRes, tiersRes] = await Promise.all([
        axios.get(`${API}/directory/my-profile`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/directory/tiers`),
      ]);
      if (profileRes.data.has_profile) {
        setProfile(profileRes.data);
        setSelectedTier(profileRes.data.subscription_tier || "");
      }
      setTiers(tiersRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegComplete = (newProfile) => {
    setProfile(newProfile);
    setActiveTab("subscription");
  };

  const handleSubscribe = async () => {
    if (!selectedTier) return;
    setSubscribing(true);
    setSubMessage("");
    try {
      const res = await axios.post(
        `${API}/directory/subscribe`,
        { tier: selectedTier },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else if (res.data.activated) {
        setProfile(res.data.profile);
        setSubMessage("Subscription activated! Your profile is now live.");
        setTimeout(() => setSubMessage(""), 4000);
      }
    } catch (err) {
      setSubMessage(err.response?.data?.detail || "Subscription failed");
    } finally {
      setSubscribing(false);
    }
  };

  // Not logged in
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <NavBar navigate={navigate} />
        <div className="container mx-auto px-4 py-20 text-center">
          <Shield className="w-16 h-16 text-blue-600 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-slate-800 mb-3" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
            Tax Pro Portal
          </h1>
          <p className="text-slate-500 mb-8 max-w-md mx-auto">
            Sign in or create an account to register as a tax professional and get your practice listed.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate("/auth")} className="bg-blue-600 hover:bg-blue-700 text-white px-8 h-11">
              Sign In / Register
            </Button>
            <Button variant="outline" onClick={() => navigate("/find-tax-pro")}>
              Browse Directory
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const isActive = profile?.monthly_fee_status === "active";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <NavBar navigate={navigate} />

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "Space Grotesk, sans-serif" }} data-testid="portal-title">
            Tax Pro Portal
          </h1>
          <p className="text-slate-500 mt-1">
            {profile ? "Manage your professional listing" : "Register to list your practice"}
          </p>
        </div>

        {/* Status banner */}
        {profile && (
          <div className={`mb-6 px-4 py-3 rounded-lg flex items-center justify-between ${
            isActive ? "bg-green-50 border border-green-200" : "bg-amber-50 border border-amber-200"
          }`} data-testid="status-banner">
            <div className="flex items-center gap-2">
              <div className={`w-2.5 h-2.5 rounded-full ${isActive ? "bg-green-500" : "bg-amber-500"}`} />
              <span className={`text-sm font-medium ${isActive ? "text-green-700" : "text-amber-700"}`}>
                {isActive
                  ? `Listing Active — ${profile.subscription_tier === "lifetime" ? "Lifetime" : profile.subscription_tier === "yearly" ? "Yearly" : "Monthly"} Plan`
                  : "Listing Pending — Select a plan to go live"}
              </span>
            </div>
            {isActive && (
              <Button variant="ghost" size="sm" onClick={() => navigate("/find-tax-pro")} className="text-green-700" data-testid="view-directory">
                <Eye className="w-4 h-4 mr-1" /> View in Directory
              </Button>
            )}
          </div>
        )}

        {/* No profile yet: show registration */}
        {!profile && (
          <Card>
            <CardHeader>
              <CardTitle>Create Your Professional Profile</CardTitle>
            </CardHeader>
            <CardContent>
              <RegistrationForm token={token} onComplete={handleRegComplete} />
            </CardContent>
          </Card>
        )}

        {/* Has profile: show tabs */}
        {profile && (
          <>
            {/* Tab nav */}
            <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 border border-slate-200 shadow-sm" data-testid="portal-tabs">
              {[
                { key: "profile", label: "Edit Profile", icon: Pencil },
                { key: "subscription", label: "Subscription", icon: Crown },
                { key: "leads", label: "Leads", icon: Inbox },
                { key: "preview", label: "Preview", icon: Eye },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.key
                      ? "bg-blue-600 text-white shadow-sm"
                      : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  }`}
                  data-testid={`tab-${tab.key}`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Profile tab */}
            {activeTab === "profile" && (
              <Card>
                <CardHeader><CardTitle>Edit Your Profile</CardTitle></CardHeader>
                <CardContent>
                  <ProfileEditor profile={profile} token={token} onUpdate={(p) => setProfile(p)} />
                </CardContent>
              </Card>
            )}

            {/* Subscription tab */}
            {activeTab === "subscription" && tiers && (
              <div className="space-y-6">
                <Card>
                  <CardHeader><CardTitle>Choose Your Plan</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {Object.entries(tiers).map(([key, info]) => (
                        <TierCard
                          key={key} tier={key} info={info}
                          selected={selectedTier} onSelect={setSelectedTier}
                          current={profile.subscription_tier}
                        />
                      ))}
                    </div>

                    {subMessage && (
                      <div className={`mt-4 px-4 py-3 rounded-lg text-sm ${
                        subMessage.includes("activated") || subMessage.includes("success")
                          ? "bg-green-50 border border-green-200 text-green-700"
                          : "bg-red-50 border border-red-200 text-red-700"
                      }`}>{subMessage}</div>
                    )}

                    <Button
                      onClick={handleSubscribe}
                      disabled={!selectedTier || subscribing || selectedTier === profile.subscription_tier}
                      className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white h-11"
                      data-testid="subscribe-btn"
                    >
                      {subscribing ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                      ) : selectedTier === profile.subscription_tier ? (
                        "Current Plan"
                      ) : (
                        `Subscribe to ${tiers[selectedTier]?.name || "Plan"} — $${tiers[selectedTier]?.price || 0}${selectedTier === "lifetime" ? " one-time" : selectedTier === "yearly" ? "/mo (billed yearly)" : "/mo"}`
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Leads tab */}
            {activeTab === "leads" && (
              <LeadsTab token={token} />
            )}

            {/* Preview tab */}
            {activeTab === "preview" && (
              <Card>
                <CardHeader><CardTitle>Profile Preview</CardTitle></CardHeader>
                <CardContent>
                  <ProfilePreview profile={profile} />
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
}


function ProfilePreview({ profile }) {
  const TYPE_CONFIG = {
    enrolled_agent: { label: "Enrolled Agent", color: "bg-emerald-100 text-emerald-800 border-emerald-200", badgeColor: "bg-emerald-600", icon: Shield },
    cpa: { label: "CPA", color: "bg-blue-100 text-blue-800 border-blue-200", badgeColor: "bg-blue-600", icon: BookOpen },
    tax_attorney: { label: "Tax Attorney", color: "bg-purple-100 text-purple-800 border-purple-200", badgeColor: "bg-purple-600", icon: Scale },
  };
  const config = TYPE_CONFIG[profile.type] || TYPE_CONFIG.enrolled_agent;
  const IconComponent = config.icon;
  const initials = profile.name.split(" ").filter((p) => !p.includes(",") && !p.includes(".")).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

  return (
    <div className="max-w-md mx-auto" data-testid="profile-preview">
      <p className="text-sm text-slate-400 text-center mb-4">This is how your listing appears in the directory</p>
      <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className={`h-1.5 ${config.badgeColor}`} />
        {profile.is_featured && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-1.5 flex items-center gap-1.5">
            <Crown className="w-3.5 h-3.5 text-amber-600" />
            <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Featured Professional</span>
          </div>
        )}
        <div className="p-5">
          <div className="flex gap-4 mb-4">
            <div className={`w-14 h-14 rounded-xl ${config.badgeColor} flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
              {initials}
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-lg">{profile.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${config.color}`}>
                  <IconComponent className="w-3 h-3" />{config.label}
                </span>
                <span className="text-xs text-slate-400">{profile.years_experience}yr exp</span>
              </div>
            </div>
          </div>
          {profile.rating > 0 && (
            <div className="flex items-center gap-1.5 mb-3">
              {[1,2,3,4,5].map((i) => (
                <Star key={i} className={`w-4 h-4 ${i <= Math.round(profile.rating) ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
              ))}
              <span className="text-sm font-semibold text-slate-700">{profile.rating}</span>
              <span className="text-sm text-slate-400">({profile.review_count})</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-sm text-slate-600 mb-3">
            <MapPin className="w-4 h-4 text-slate-400" />{profile.city}, {profile.state}
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(profile.specialties || []).map((s, i) => (
              <span key={i} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-md">{s}</span>
            ))}
          </div>
          <p className="text-sm text-slate-500 leading-relaxed line-clamp-3">{profile.bio}</p>
          <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
            <Button variant="outline" className="flex-1 text-sm h-9"><Phone className="w-3.5 h-3.5 mr-1.5" />Call</Button>
            <Button variant="outline" className="flex-1 text-sm h-9"><Mail className="w-3.5 h-3.5 mr-1.5" />Email</Button>
            {profile.website && (
              <Button variant="outline" className="text-sm h-9 px-3"><Globe className="w-3.5 h-3.5" /></Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function NavBar({ navigate }) {
  return (
    <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate("/")}>
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-800" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
              Tax Helper Pros
            </span>
          </div>
          <div className="flex items-center space-x-3">
            <Button variant="ghost" onClick={() => navigate("/find-tax-pro")} className="text-slate-600">
              <ArrowLeft className="w-4 h-4 mr-2" /> Directory
            </Button>
            <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-600">Home</Button>
          </div>
        </div>
      </div>
    </nav>
  );
}
